/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { logError } from '../utils/logger';
import {
  CreateProviderResponseData,
  CreateReviewData,
  MultipleRatingsRequest,
  MultipleRatingsResponse,
  OverallRating,
  PaginationParams,
  ProviderResponse,
  ProviderResponseDisplay,
  Review,
  ReviewAnalytics,
  ReviewsResponse,
  ReviewsResponseById,
  UpdateReviewData,
} from '../utils/type';

// Reviewer info interface for backend data structure
const SERVICE_URL = import.meta.env.VITE_APP_BACKEND_SERVICE;

/**
 * Create a new review with image names storage
 */
export const createReview = async (data: CreateReviewData): Promise<Review> => {
  try {
    console.log('Creating review with data:', {
      ...data,
      imageCount: data.imageNames?.length || 0,
      hasImageNames: !!(data.imageNames && data.imageNames.length > 0),
    });

    // Validate required fields
    // Provider ID is optional - use default if not provided
    if (!data.providerId || data.providerId.trim() === '') {
      data.providerId = 'default-provider';
    }
    if (!data.serviceId || data.serviceId.trim() === '') {
      throw new Error('Service ID is required and cannot be empty');
    }
    // Booking ID is optional for service-level reviews — don't send it if missing
    // (previously a fake ID was generated, which caused backend lookup failures)
    if (!data.rating || data.rating < 1 || data.rating > 5) {
      throw new Error('Rating must be between 1 and 5');
    }
    if (!data.title || data.title.trim() === '') {
      throw new Error('Title is required and cannot be empty');
    }
    if (!data.comment || data.comment.trim() === '') {
      throw new Error('Review comment is required and cannot be empty');
    }
    if (data.comment.trim().length < 10 || data.comment.trim().length > 2000) {
      throw new Error('Review comment must be between 10 and 2000 characters');
    }

    // Prepare review payload with image names for backend storage
    const reviewPayload: any = {
      providerId: data.providerId.trim(),
      serviceId: data.serviceId.trim(),
      rating: Math.round(data.rating), // Ensure integer rating
      title: data.title.trim(),
      comment: data.comment.trim(), // Backend expects comment field
      date: data.date || new Date().toISOString(),
      imageNames: data.imageNames || [], // Primary field: image names for database storage
      imageUrls: data.imageUrls || [], // Optional: for backward compatibility
    };

    // Only include bookingId when it's a real booking reference
    if (
      data.bookingId &&
      data.bookingId.trim() !== '' &&
      data.bookingId !== 'undefined' &&
      data.bookingId !== 'null' &&
      !data.bookingId.startsWith('service-review-')
    ) {
      reviewPayload.bookingId = data.bookingId.trim();
    }

    // Quote-flow job reference. This function rebuilds the payload field by
    // field rather than spreading `data`, so anything not copied here never
    // reaches the API — jobId has to be forwarded explicitly.
    const jobIdValue = (data as any).jobId;
    if (
      jobIdValue &&
      String(jobIdValue).trim() !== '' &&
      jobIdValue !== 'undefined' &&
      jobIdValue !== 'null'
    ) {
      reviewPayload.jobId = String(jobIdValue).trim();
    }

    // Include businessId if provided
    if ((data as any).businessId && (data as any).businessId.trim() !== '') {
      reviewPayload.businessId = (data as any).businessId.trim();
    }

    // Backend requires all rating fields - ensure they are always present and valid
    // Use individual ratings if provided, otherwise use overall rating as fallback (minimum 1)
    const fallbackRating = Math.max(Math.round(data.rating), 1); // Ensure minimum rating of 1

    reviewPayload.serviceRating =
      data.serviceRating && data.serviceRating >= 1 && data.serviceRating <= 5
        ? Math.round(data.serviceRating)
        : fallbackRating;

    reviewPayload.qualityRating =
      data.qualityRating && data.qualityRating >= 1 && data.qualityRating <= 5
        ? Math.round(data.qualityRating)
        : fallbackRating;

    reviewPayload.valueRating =
      data.valueRating && data.valueRating >= 1 && data.valueRating <= 5
        ? Math.round(data.valueRating)
        : fallbackRating;

    reviewPayload.communicationRating =
      data.communicationRating &&
      data.communicationRating >= 1 &&
      data.communicationRating <= 5
        ? Math.round(data.communicationRating)
        : fallbackRating;

    reviewPayload.timelinessRating =
      data.timelinessRating &&
      data.timelinessRating >= 1 &&
      data.timelinessRating <= 5
        ? Math.round(data.timelinessRating)
        : fallbackRating;
    // Add user information if available
    if (data.userName) {
      reviewPayload.userName = data.userName.trim();
    }
    if (data.userEmail) {
      reviewPayload.userEmail = data.userEmail.trim();
    }
    if (data.userProfileImage) {
      reviewPayload.userProfileImage = data.userProfileImage.trim();
    }
    if (typeof data.isVerified === 'boolean') {
      reviewPayload.isVerified = data.isVerified;
    }

    // Include userId and reviewerInfo when provided so that queries by user return the new review
    if ((data as any).userId) {
      reviewPayload.userId = (data as any).userId;
    }
    if (data.userName || data.userEmail) {
      reviewPayload.reviewerInfo = {
        name: (data.userName || '').trim() || 'Anonymous User',
        email: (data.userEmail || '').trim() || 'no-email@example.com',
      };
    }

    console.log('Sending review payload to backend:', {
      ...reviewPayload,
      imageNamesCount: reviewPayload.imageNames.length,
      ratingFields: {
        rating: reviewPayload.rating,
        serviceRating: reviewPayload.serviceRating,
        qualityRating: reviewPayload.qualityRating,
        valueRating: reviewPayload.valueRating,
        communicationRating: reviewPayload.communicationRating,
        timelinessRating: reviewPayload.timelinessRating,
      },
    });

    const response = await apiClient.post(
      SERVICE_URL + Path.reviews,
      reviewPayload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('Review created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating review:', error);

    // Enhanced error logging for debugging
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request error:', error.request);
    } else {
      console.error('Error message:', error.message);
    }

    logError('Error creating review:', { error });
    throw error;
  }
};

export const createReviewVote = async (
  reviewId: string,
  voteType: 'helpful' | 'not_helpful'
): Promise<Review> => {
  try {
    console.log('Creating review vote with data:', { reviewId, voteType });

    const response = await apiClient.post(
      `${SERVICE_URL}${Path.reviews}/${reviewId}/vote`,
      {
        voteType,
      }
    );
    console.log('Review vote created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating review vote:', error);

    // Enhanced error logging for debugging
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request error:', error.request);
    } else {
      console.error('Error message:', error.message);
    }

    logError('Error creating review:', error);
    throw error;
  }
};

/**
 * Get all reviews with pagination
 */
export const getAllReviews = async (
  params: PaginationParams = {}
): Promise<ReviewsResponse> => {
  try {
    const { page = 1, limit = 3, userId } = params;
    console.log(
      `Fetching reviews - page: ${page}, limit: ${limit}, userId: ${userId}`
    );

    const response = await apiClient.get(`${SERVICE_URL}${Path.reviews}`, {
      params: {
        page,
        limit,
        ...(userId ? { userId } : {}),
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching reviews:', error);
    logError('Error fetching reviews:', error);
    throw error;
  }
};

/**
 * Get a specific review by ID
 */
export const getReviewById = async (reviewId: string): Promise<Review> => {
  try {
    console.log(`Fetching review with ID: ${reviewId}`);
    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/${reviewId}`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching review ${reviewId}:`, error);
    logError(`Error fetching review ${reviewId}:`, error);
    throw error;
  }
};

/**
 * Update a review with image names support
 */
export const updateReview = async (
  reviewId: string,
  data: UpdateReviewData
): Promise<Review> => {
  try {
    console.log(`Updating review ${reviewId} with data:`, {
      ...data,
      imageCount: data.imageNames?.length || 0,
      hasImageNames: !!(data.imageNames && data.imageNames.length > 0),
    });

    // Prepare update payload with image names
    const updatePayload = {
      ...data,
      imageNames: data.imageNames || [], // Ensure imageNames is always an array
      imageUrls: data.imageUrls, // Keep for backward compatibility
    };

    console.log('Sending update payload to backend:', {
      reviewId,
      imageNamesCount: updatePayload.imageNames.length,
    });

    const response = await apiClient.put(
      `${SERVICE_URL}${Path.reviews}/${reviewId}`,
      updatePayload
    );

    console.log('Review updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error(`Error updating review ${reviewId}:`, error);
    logError(`Error updating review ${reviewId}:`, error);
    throw error;
  }
};

/**
 * Delete a review with force option to bypass validation
 */
export const deleteReview = async (
  reviewId: string,
  force: boolean = false
): Promise<void> => {
  try {
    console.log(`Deleting review with ID: ${reviewId}, force: ${force}`);

    // Validate reviewId format
    if (!reviewId || reviewId.trim() === '' || reviewId === 'undefined') {
      throw new Error('Invalid review ID provided');
    }

    // Additional validation for MongoDB ObjectId format (24 hex characters)
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    if (!objectIdRegex.test(reviewId)) {
      console.warn(
        `Review ID ${reviewId} does not match MongoDB ObjectId format`
      );
    }

    let lastError: any = null;

    try {
      const response = await apiClient.delete(
        `${SERVICE_URL}${Path.reviews}/${reviewId}`
      );
      console.log(`✅ Review deleted successfully via :`, response.status);
      return;
    } catch (error: any) {
      console.log(
        `❌ DELETE failed for review ${reviewId}:`,
        error.response?.status,
        error.response?.data?.message
      );
      lastError = error;

      // If it's a 404, the review might not exist
      if (error.response?.status === 404) {
        console.log('Review not found, considering it already deleted');
        return;
      }
    }

    // If all endpoints failed, throw the last error
    throw lastError;
  } catch (error: any) {
    console.error(`Error deleting review ${reviewId}:`, error);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
    });
    logError(`Error deleting review ${reviewId}:`, error);
    throw error;
  }
};

/**
 * Fix a review that's missing required reviewerInfo field
 */
export const fixReviewerInfo = async (reviewId: string): Promise<void> => {
  try {
    console.log(`Fixing reviewerInfo for review ${reviewId}`);

    // Get the current review data
    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/${reviewId}`
    );
    const reviewData = response.data;

    // Check if reviewerInfo is missing or incomplete
    if (!reviewData.reviewerInfo || !reviewData.reviewerInfo.name) {
      const reviewerInfo = {
        name: reviewData.userName || reviewData.name || 'Anonymous User',
        email:
          reviewData.userEmail || reviewData.email || 'no-email@example.com',
      };

      console.log('Updating review with reviewerInfo:', reviewerInfo);

      // Update the review with the required reviewerInfo
      await apiClient.put(`${SERVICE_URL}${Path.reviews}/${reviewId}`, {
        reviewerInfo: reviewerInfo,
      });

      console.log('Successfully fixed reviewerInfo for review:', reviewId);
    } else {
      console.log(
        'Review already has valid reviewerInfo:',
        reviewData.reviewerInfo
      );
    }
  } catch (error: any) {
    console.error(`Error fixing reviewerInfo for review ${reviewId}:`, error);
    logError(`Error fixing reviewerInfo for review ${reviewId}:`, error);
    throw error;
  }
};

/**
 * Get reviews by user ID
 */
export const getReviewsByUserId = async (
  userId: string,
  params: PaginationParams = {}
): Promise<ReviewsResponse> => {
  try {
    const { page = 1, limit = 10, rating } = params;
    console.log(
      `Fetching reviews for user ${userId} - page: ${page}, limit: ${limit}`
    );

    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/logs/user-activity`,
      {
        params: {
          page,
          limit,
          rating,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching reviews for user ${userId}:`, error);
    logError(`Error fetching reviews for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Get reviews by  ID
 */
export const getReviewsById = async (
  id: string
): Promise<ReviewsResponseById> => {
  try {
    console.log(`Fetching reviews for id ${id}`);

    const response = await apiClient.get(`${SERVICE_URL}${Path.reviews}/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching reviews for id ${id}:`, error);
    logError(`Error fetching reviews for id ${id}:`, error);
    throw error;
  }
};

/**
 * Get reviews by service ID
 */
export const getReviewsByServiceId = async (
  serviceId: string,
  params: PaginationParams = {}
): Promise<ReviewsResponse> => {
  try {
    const { page = 1, limit = 10, userId } = params;
    console.log(
      `Fetching reviews for service ${serviceId} - page: ${page}, limit: ${limit}`
    );

    const response = await apiClient.get(`${SERVICE_URL}${Path.reviews}`, {
      params: {
        page,
        limit,
        serviceId,
        ...(userId ? { customerId: userId } : {}),
      },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching reviews for service ${serviceId}:`, error);
    logError(`Error fetching reviews for service ${serviceId}:`, error);
    throw error;
  }
};

// // GET ALL REVIEWS FOR PROVIDER
export const getAllReviewsForProvider = async ({ page, limit, id }: any) => {
  try {
    const res = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/?page=${page}&limit=${limit}&businessId=${id}`
    );
    return res.data;
  } catch (error: any) {
    console.log('Error get all review ', error);
  }
};
// ===== NEW API ENDPOINTS =====

// Analytics and Rating interfaces

/**
 * Get review analytics
 */
export const getReviewAnalytics = async (
  params: {
    providerId?: string;
    businessId?: string;
    serviceId?: string;
    startDate?: string;
    endDate?: string;
  } = {}
): Promise<ReviewAnalytics> => {
  try {
    console.log('Fetching review analytics:', params);

    const queryParams = new URLSearchParams();
    if (params.providerId) queryParams.append('providerId', params.providerId);
    if (params.businessId) queryParams.append('businessId', params.businessId);
    if (params.serviceId) queryParams.append('serviceId', params.serviceId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/analytics?${queryParams.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching review analytics:', error);
    logError('Error fetching review analytics:', error);
    throw error;
  }
};

/**
 * Get overall rating summary
 */
export const getOverallRating = async (params: {
  providerId?: string;
  businessId?: string;
  serviceId?: string;
}): Promise<OverallRating> => {
  try {
    console.log('Fetching overall rating:', params);

    const queryParams = new URLSearchParams();
    if (params.providerId) queryParams.append('providerId', params.providerId);
    if (params.businessId) queryParams.append('businessId', params.businessId);
    if (params.serviceId) queryParams.append('serviceId', params.serviceId);

    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/rating/overall?${queryParams.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching overall rating:', error);
    logError('Error fetching overall rating:', error);
    throw error;
  }
};

/**
 * Get multiple rating summaries
 */
export const getMultipleRatings = async (
  data: MultipleRatingsRequest
): Promise<MultipleRatingsResponse> => {
  try {
    console.log('Fetching multiple ratings:', data);

    const queryParams = new URLSearchParams();
    if (data.serviceIds?.length) {
      queryParams.append('serviceIds', data.serviceIds.join(','));
    }
    if (data.providerIds?.length) {
      queryParams.append('providerIds', data.providerIds.join(','));
    }

    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/rating/multiple?${queryParams.toString()}`
    );

    return response.data;
  } catch (error: any) {
    console.error('Error fetching multiple ratings:', error);
    // logError('Error fetching multiple ratings:', error);
    throw error;
  }
};

// // GET ALL REVIEWS FOR PROVIDER
export const getReviewOverall = async (id: string) => {
  try {
    const res = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/rating/overall?businessId=${id}`
    );
    return res.data;
  } catch (error: any) {
    console.log('Error get review overall ', error);
  }
};

/**
 * Get review by booking id
 */
export const getReviewByBookingId = async (id: string) => {
  try {
    const res = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/booking/${id}/response`
    );
    return res.data;
  } catch (error: any) {
    console.log('Error get review by booking id ', error);
  }
};
/**
 * Add provider response to a review
 */
export const addProviderResponse = async (
  reviewId: string,
  data: CreateProviderResponseData
): Promise<ProviderResponse> => {
  try {
    console.log(`Adding provider response to review ${reviewId}:`, data);

    const response = await apiClient.post(
      `${SERVICE_URL}${Path.reviews}/${reviewId}/response`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('Provider response added successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error(
      `Error adding provider response to review ${reviewId}:`,
      error
    );
    logError(`Error adding provider response to review ${reviewId}:`, error);
    throw error;
  }
};

/**
 * Get provider responses for a review
 */
export const getProviderResponsesForReview = async (
  reviewId: string
): Promise<ProviderResponseDisplay> => {
  try {
    console.log(`Fetching provider responses for review ${reviewId}`);
    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/${reviewId}/response`
    );
    const raw = response.data;

    console.log('Raw provider responses data:', raw);

    // Normalize responses - handle various API response structures
    let normalizedResponses: ProviderResponse[] = [];

    if (Array.isArray(raw)) {
      // Direct array response
      normalizedResponses = raw;
    } else if (Array.isArray(raw?.responses)) {
      // { responses: [...] }
      normalizedResponses = raw.responses;
    } else if (Array.isArray(raw?.data)) {
      // { data: [...] }
      normalizedResponses = raw.data;
    } else if (Array.isArray(raw?.response)) {
      // { response: [...] } - response is an array
      normalizedResponses = raw.response;
    } else if (Array.isArray(raw?.providerResponses)) {
      // { providerResponses: [...] }
      normalizedResponses = raw.providerResponses;
    } else if (raw?.response && typeof raw.response === 'object') {
      // { response: {...} } - single response object
      normalizedResponses = [raw.response];
    } else if (
      raw?.providerResponse &&
      typeof raw.providerResponse === 'object'
    ) {
      // { providerResponse: {...} } - single response object
      normalizedResponses = [raw.providerResponse];
    } else if (
      raw &&
      typeof raw === 'object' &&
      raw.response &&
      typeof raw.response === 'string'
    ) {
      // The raw object itself is a single response with response as string
      normalizedResponses = [raw as ProviderResponse];
    }

    console.log('Normalized provider responses:', normalizedResponses);

    return raw;
  } catch (error: any) {
    console.error(
      `Error fetching provider responses for review ${reviewId}:`,
      error
    );
    logError(
      `Error fetching provider responses for review ${reviewId}:`,
      error
    );
    throw error;
  }
};

// ===== ENHANCED REVIEW FILTERING AND SEARCH =====

export interface ReviewFilters {
  providerId?: string;
  businessId?: string;
  serviceId?: string;
  userId?: string;
  rating?: number;
  minRating?: number;
  maxRating?: number;
  startDate?: string;
  endDate?: string;
  hasImages?: boolean;
  isVerified?: boolean;
  sortBy?: 'date' | 'rating' | 'helpful' | 'recent';
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

/**
 * Get filtered reviews with advanced search
 */
export const getFilteredReviews = async (
  filters: ReviewFilters,
  pagination: PaginationParams = {}
): Promise<ReviewsResponse> => {
  try {
    const { page = 1, limit = 10 } = pagination;
    console.log('Fetching filtered reviews:', { filters, page, limit });

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}?${queryParams.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching filtered reviews:', error);
    logError('Error fetching filtered reviews:', error);
    throw error;
  }
};

/**
 * Bulk operations for reviews
 */
export const bulkDeleteReviews = async (
  reviewIds: string[]
): Promise<{ deleted: number; failed: string[] }> => {
  try {
    console.log(`Bulk deleting ${reviewIds.length} reviews:`, reviewIds);

    const response = await apiClient.delete(
      `${SERVICE_URL}${Path.reviews}/bulk`,
      {
        data: { reviewIds },
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('Bulk delete completed:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error in bulk delete reviews:', error);
    logError('Error in bulk delete reviews:', error);
    throw error;
  }
};

/**
 * Get user review activity logs
 */
export const getUserReviewActivityLogs = async (
  params: {
    page?: number;
    limit?: number;
    sortOrder?: 'asc' | 'desc';
    status?: string;
    userId?: string;
  } = {}
): Promise<ReviewsResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      sortOrder = 'desc',
      status = 'approved',
      userId,
    } = params;

    console.log('Fetching user review activity logs:', {
      page,
      limit,
      sortOrder,
      status,
      userId,
    });

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortOrder,
      status,
    });

    if (userId) {
      queryParams.append('userId', userId);
    }

    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}service/logs/user-activity?${queryParams.toString()}`
    );

    console.log('User activity logs response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching user review activity logs:', error);
    logError('Error fetching user review activity logs:', error);
    throw error;
  }
};

/**
 * Export reviews data
 */
export const exportReviews = async (
  filters: ReviewFilters = {},
  format: 'csv' | 'json' = 'csv'
): Promise<Blob> => {
  try {
    console.log('Exporting reviews:', { filters, format });

    const queryParams = new URLSearchParams({ format });

    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await apiClient.get(
      `${SERVICE_URL}${Path.reviews}/export?${queryParams.toString()}`,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Error exporting reviews:', error);
    logError('Error exporting reviews:', error);
    throw error;
  }
};
